import type { FunctionComponent } from 'react';
import { memo, useCallback, useRef } from 'react';

import {
    useChatComposerActive,
    useChatComposerCallbacks,
    useChatComposerConversation,
    useChatComposerInput,
} from '../chat-composer-contexts';
import { useChatImplementation } from '../chat-implementation';
import { ChatIdleSlotOutlet } from './ChatIdleSlotOutlet';
import { ChatPromptOutlet } from './ChatPromptOutlet';
import { ChatStarterPrompts } from './ChatStarterPrompts';
import { PillChatComposer } from './PillChatComposer';

export const CollapsedComposer: FunctionComponent<{
    readonly onOpenChange: (open: boolean) => void;
}> = memo(({ onOpenChange }) => {
    const {
        addAttachmentFiles,
        addKnowledgeBaseAttachments,
        handleDraftChange,
        handleKeyDown,
        open,
        removeAttachment,
        submit,
        toggleAttachmentKnowledgeBase,
    } = useChatComposerCallbacks();
    const { collapsedComposerExpandSignal, promptChatContext } = useChatComposerActive();
    const { attachments, draft, transcription } = useChatComposerInput();
    const conversation = useChatComposerConversation();
    const { useIsConversationEmpty } = useChatImplementation();
    const containerRef = useRef<HTMLDivElement>(null);
    const focusComposer = useCallback(() => {
        requestAnimationFrame(() => containerRef.current?.querySelector('textarea')?.focus());
    }, []);

    const { isStreaming } = conversation;
    const showStarterPrompts =
        promptChatContext === null &&
        conversation.threadId === null &&
        !conversation.isThreadPending &&
        !isStreaming &&
        draft.trim().length === 0;
    // Mirrors the chat body's empty-conversation gate so collapsing the chat keeps the same prompt card.
    const isConversationEmpty = useIsConversationEmpty({ isEnabled: promptChatContext !== null });
    const topSlot = showStarterPrompts ? (
        <ChatStarterPrompts surface="pill" onPicked={focusComposer} />
    ) : promptChatContext !== null && !isStreaming && isConversationEmpty ? (
        <ChatPromptOutlet />
    ) : null;

    return (
        <div ref={containerRef}>
            <PillChatComposer
                attachments={attachments}
                canStop={conversation.canStop}
                draft={draft}
                idleSlot={<ChatIdleSlotOutlet />}
                isExpanded={false}
                isStreaming={isStreaming}
                openOnFocus={showStarterPrompts}
                openRequestSignal={collapsedComposerExpandSignal}
                topSlot={topSlot}
                transcription={transcription}
                onAttachmentFilesSelected={addAttachmentFiles}
                onAttachmentKnowledgeBaseSelected={addKnowledgeBaseAttachments}
                onAttachmentRemove={removeAttachment}
                onAttachmentToggleKnowledgeBase={toggleAttachmentKnowledgeBase}
                onDraftChange={handleDraftChange}
                onKeyDown={handleKeyDown}
                onOpen={open}
                onOpenChange={onOpenChange}
                onStop={conversation.stop}
                onSubmit={submit}
            />
        </div>
    );
});
